import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Clock, CalendarRange, FileClock, AlertTriangle, ChevronRight } from 'lucide-react';
import { StatusChip } from './TimeHistory';

const API = `${window.__BACKEND_URL__}/api`;

const utcDate = (v) => new Date(typeof v === 'string' && !v.endsWith('Z') && !v.includes('+') ? `${v}Z` : v);
const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

const mondayOf = (d) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7));
  return x;
};

const entryHours = (e, now) => {
  const start = utcDate(e.clock_in);
  const end = e.clock_out ? utcDate(e.clock_out) : now;
  return Math.max(0, (end - start) / 3600000);
};

export default function WorkerSummary({ token, clockStatus }) {
  const navigate = useNavigate();
  const [entries, setEntries] = useState([]);
  const [breaks, setBreaks] = useState({ paid_minutes: 0, unpaid_minutes: 0 });
  const [thresholds, setThresholds] = useState({ ot_weekly_threshold: 44, ot_daily_threshold: 8 });
  const [unsubmitted, setUnsubmitted] = useState(0);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 60000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (!token) return;
    const auth = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/time/my-entries?days=8`, auth).then(({ data }) => setEntries(data.entries || [])).catch(() => {});
    axios.get(`${API}/time/break/current`, auth).then(({ data }) => setBreaks(data.today || {})).catch(() => {});
    axios.get(`${API}/company/overtime-settings`, auth).then(({ data }) => setThresholds(data)).catch(() => {});
    axios.get(`${API}/time/my-unsubmitted`, auth).then(({ data }) => setUnsubmitted(data.total ?? (data.entries || []).length)).catch(() => {});
  }, [token, clockStatus?.clocked_in]); // eslint-disable-line

  // ── Today ──────────────────────────────────────────────────────────────
  const todayStart = new Date(now); todayStart.setHours(0, 0, 0, 0);
  const todayEntries = entries.filter((e) => utcDate(e.clock_in) >= todayStart);
  const breakHrs = ((breaks.paid_minutes || 0) + (breaks.unpaid_minutes || 0)) / 60;
  const todayGross = todayEntries.reduce((s, e) => s + entryHours(e, now), 0);
  const todayPayable = Math.max(0, todayGross - (breaks.unpaid_minutes || 0) / 60);
  const dailyThr = thresholds.ot_daily_threshold ?? 8;
  const todayOt = Math.max(0, todayPayable - dailyThr);
  const todayReg = todayPayable - todayOt;
  const openExceptions = entries.reduce((s, e) => s + (e.open_exception_count || 0), 0);

  let liveTimer = null;
  if (clockStatus?.clocked_in && clockStatus.clock_in_time) {
    const mins = Math.max(0, Math.floor((now - utcDate(clockStatus.clock_in_time)) / 60000));
    liveTimer = `${Math.floor(mins / 60)}h ${mins % 60}m`;
  }
  const activeLabel = clockStatus?.job_title || clockStatus?.job_number || clockStatus?.project_name || clockStatus?.area_label;

  // ── This week ──────────────────────────────────────────────────────────
  const weekStart = mondayOf(now);
  const dayHours = [0, 0, 0, 0, 0, 0, 0];
  entries.forEach((e) => {
    const d = utcDate(e.clock_in);
    if (d >= weekStart) dayHours[(d.getDay() + 6) % 7] += entryHours(e, now);
  });
  const weekTotal = dayHours.reduce((a, b) => a + b, 0);
  const weeklyThr = thresholds.ot_weekly_threshold ?? 44;
  const pct = weeklyThr > 0 ? (weekTotal / weeklyThr) * 100 : 0;
  const barColor = pct >= 100 ? '#EF4444' : pct >= 75 ? '#FACC15' : '#00C896';
  const maxDay = Math.max(8, ...dayHours);

  const recent = entries.slice(0, 5);

  return (
    <>
      {/* ===== TODAY ===== */}
      <div className="ht-card p-4 mb-4" data-testid="today-summary-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4" style={{ color: '#1E88E5' }} />
            <h2 className="text-sm font-bold text-white">Today</h2>
          </div>
          {openExceptions > 0 && (
            <span className="flex items-center gap-1 text-[11px] px-2 py-0.5 rounded-full font-semibold"
              style={{ background: 'rgba(255,120,0,0.15)', color: '#FB923C' }}
              data-testid="today-exception-badge">
              <AlertTriangle className="w-3 h-3" /> {openExceptions} exception{openExceptions === 1 ? '' : 's'}
            </span>
          )}
        </div>
        <p className="text-lg font-bold" style={{ color: clockStatus?.clocked_in ? '#00C896' : '#64748B' }} data-testid="today-clock-status">
          {clockStatus?.clocked_in ? `Clocked In — ${liveTimer}` : 'Clocked Out'}
        </p>
        {clockStatus?.clocked_in && activeLabel && (
          <p className="text-xs mt-0.5" style={{ color: '#A0AEC0' }} data-testid="today-active-job">
            Working on: <span className="text-white font-medium">{activeLabel}</span>
          </p>
        )}
        <div className="flex gap-2 mt-3" data-testid="today-hours-breakdown">
          {[
            ['Regular', todayReg, '#fff'],
            ['OT', todayOt, todayOt > 0 ? '#FFA000' : '#64748B'],
            ['Break', breakHrs, '#60A5FA'],
          ].map(([label, v, color]) => (
            <div key={label} className="flex-1 rounded-xl px-3 py-2 text-center" style={{ background: '#0B1321', border: '1px solid #2D3748' }}>
              <p className="text-base font-bold" style={{ color }}>{v.toFixed(1)}</p>
              <p className="text-[10px] uppercase tracking-wide" style={{ color: '#64748B' }}>{label} hrs</p>
            </div>
          ))}
        </div>
      </div>

      {/* ===== THIS WEEK ===== */}
      <div className="ht-card p-4 mb-4" data-testid="week-summary-card">
        <div className="flex items-center gap-2 mb-3">
          <CalendarRange className="w-4 h-4" style={{ color: '#1E88E5' }} />
          <h2 className="text-sm font-bold text-white">This Week</h2>
        </div>
        <div className="flex items-end justify-between gap-1.5 h-20 mb-3" data-testid="week-bars">
          {dayHours.map((h, i) => (
            <div key={i} className="flex-1 flex flex-col items-center justify-end h-full">
              {h > 0 && <span className="text-[9px] mb-0.5" style={{ color: '#A0AEC0' }}>{h.toFixed(1)}</span>}
              <div className="w-full rounded-t-md"
                style={{ height: `${Math.max(h > 0 ? 8 : 2, (h / maxDay) * 100)}%`,
                         background: h > 0 ? '#1E88E5' : '#1F2D3F' }} />
              <span className="text-[9px] mt-1" style={{ color: (now.getDay() + 6) % 7 === i ? '#1E88E5' : '#4A5568' }}>{DAY_LABELS[i]}</span>
            </div>
          ))}
        </div>
        <div className="w-full h-2 rounded-full overflow-hidden mb-1.5" style={{ background: '#1F2D3F' }}>
          <div className="h-full rounded-full transition-all"
            style={{ width: `${Math.min(100, pct)}%`, background: barColor }}
            data-testid="week-ot-progress" />
        </div>
        <p className="text-xs" style={{ color: '#A0AEC0' }} data-testid="week-ot-label">
          <span className="font-bold" style={{ color: barColor }}>{weekTotal.toFixed(1)} / {weeklyThr} hrs</span>
          {' — '}{Math.round(pct)}% of OT limit
        </p>
      </div>

      {/* ===== MY TIMESHEETS ===== */}
      <div className="ht-card p-4 mb-4" data-testid="my-timesheets-card">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            <FileClock className="w-4 h-4" style={{ color: '#1E88E5' }} />
            <h2 className="text-sm font-bold text-white">My Timesheets</h2>
            {unsubmitted > 0 && (
              <span className="text-[10px] px-1.5 py-0.5 rounded-full font-bold"
                style={{ background: 'rgba(255,200,0,0.15)', color: '#FACC15' }}
                data-testid="unsubmitted-count-chip">
                {unsubmitted} to certify
              </span>
            )}
          </div>
          <button onClick={() => navigate('/my-time')}
            className="flex items-center gap-0.5 text-xs font-semibold"
            style={{ color: '#1E88E5' }}
            data-testid="timesheets-view-all-btn">
            View All <ChevronRight className="w-3.5 h-3.5" />
          </button>
        </div>
        {recent.length === 0 ? (
          <p className="text-xs text-center py-4" style={{ color: '#64748B' }} data-testid="timesheets-empty">
            No shifts recorded yet this week.
          </p>
        ) : (
          <div className="space-y-1.5">
            {recent.map((e) => (
              <div key={e.id} className="flex items-center justify-between gap-2 rounded-lg px-3 py-2"
                style={{ background: '#0B1321', border: '1px solid #2D3748' }}
                data-testid={`timesheet-recent-${e.id}`}>
                <div className="min-w-0">
                  <p className="text-xs text-white font-semibold">
                    {utcDate(e.clock_in).toLocaleDateString([], { weekday: 'short', month: 'short', day: 'numeric' })}
                  </p>
                  <p className="text-[11px]" style={{ color: '#A0AEC0' }}>
                    {utcDate(e.clock_in).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    {' → '}
                    {e.clock_out ? utcDate(e.clock_out).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }) : '—'}
                    {e.clock_out && <> · {entryHours(e, now).toFixed(2)}h</>}
                  </p>
                </div>
                <StatusChip entry={e} />
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}
